import { wellFlowingTemperatureForMD } from "@/libs/calculations/pressureDropCorrelations";
import {
  buildIntervalCasingGeometry,
  calculateSealsAndIntakesCasingFriction
} from "../../casingFrictionCalculations";
import type { FluidPropertiesPipelineInputs } from "../types";
import type { FluidPropertiesContext } from "@/libs/calculations/fluidProperties/d";

// Seals -> intake: well flowing pressure and temperature at the intake.
export const calculateSealsToIntake = (
  inputs: FluidPropertiesPipelineInputs,
  prev: {
    fluidPropertiesAtSealsContext: FluidPropertiesContext;
    getPressureAtReferenceTVDPSIA: (referenceTVDFt: number) => number;
    casingFrictionLossPSIA: number;
  }
) => {
  const {
    basicData,
    correlations,
    includeCasingFrictionLoss,
    entryRows,
    bottomOfIntakesTVDFt,
    bottomOfIntakesMDFt,
    bottomOfSealsMDFt,
    bottomOfSealsTVDFt,
    primarySealOuterDiameterInches,
    effectiveCasingInteriorDiameterAtSealsIn,
    topCasingInteriorDiameterIn,
    topCasingLengthFt,
    bottomCasingInteriorDiameterIn,
    bottomCasingLengthFt,
    overrides
  } = inputs;
  const {
    fluidPropertiesAtSealsContext,
    getPressureAtReferenceTVDPSIA,
    casingFrictionLossPSIA: startingCasingFrictionLossPSIA
  } = prev;
  let casingFrictionLossPSIA = startingCasingFrictionLossPSIA;
  let sealsAndIntakesCasingFrictionPSIA = 0;

  const sealsAndIntakesPressureProperties =
    calculateSealsAndIntakesCasingFriction({
      correlations,
      bottomOfSealsMDFt,
      bottomOfSealsTVDFt,
      bottomOfIntakesMDFt,
      bottomOfIntakesTVDFt,
      entryRows,
      fluidPropertiesAtSealsContext,
      surfaceTemperatureF: basicData.surfaceTemperatureF,
      bottomholeTemperatureF: basicData.bottomholeTemperatureF,
      casingGeometry: buildIntervalCasingGeometry(
        bottomOfSealsMDFt,
        bottomOfIntakesMDFt,
        topCasingInteriorDiameterIn,
        topCasingLengthFt,
        bottomCasingInteriorDiameterIn,
        bottomCasingLengthFt,
        effectiveCasingInteriorDiameterAtSealsIn,
        primarySealOuterDiameterInches
      ),
      bottomCasingPressurePSIA: basicData.bottomCasingPressurePSIA,
      producingBottomholePressurePSIA: basicData.producingBottomholePressurePSIA
    });

  sealsAndIntakesCasingFrictionPSIA =
    sealsAndIntakesPressureProperties.frictionPressurePSIA;
  let sealsAndIntakesAppliedFrictionPSIA = 0;
  if (includeCasingFrictionLoss) {
    sealsAndIntakesAppliedFrictionPSIA = sealsAndIntakesCasingFrictionPSIA;
    casingFrictionLossPSIA += sealsAndIntakesAppliedFrictionPSIA;
  }
  let wellFlowingPressureAtIntakePSIA =
    getPressureAtReferenceTVDPSIA(bottomOfIntakesTVDFt) -
    sealsAndIntakesAppliedFrictionPSIA;
  // If user supplies the PIP (like in perfs) then it gets used at the intake
  // and at the pump
  const pumpIntakePressureProvided = overrides?.pumpIntakePressurePSIA;
  if (pumpIntakePressureProvided) {
    wellFlowingPressureAtIntakePSIA =
      overrides?.pumpIntakePressurePSIA?.value || 0;
  }

  // Intake Temperature (downhole temp where intake is located)
  const downholeIntakeTemperatureF = wellFlowingTemperatureForMD(
    bottomOfIntakesMDFt,
    basicData.datumPointFt,
    basicData.bottomholeTemperatureF,
    basicData.surfaceTemperatureF
  );
  return {
    sealsAndIntakesCasingFrictionPSIA,
    wellFlowingPressureAtIntakePSIA,
    downholeIntakeTemperatureF,
    casingFrictionLossPSIA
  };
};
